import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  MOZZOAPP_INSTITUTIONAL_LOGO_URL,
  applyInstitutionalBrandingAssets,
} from "../../services/local/institutionalBranding";
import { getPublicKioskDigitalPaymentIntent } from "../../services/supabase/kioskDigitalPaymentsService";

const APPROVED_KIOSK_PAYMENT_STATUSES = new Set(["approved", "paid", "closed"]);

const toSafeText = (value, maxLength = 240) =>
  String(value || "")
    .trim()
    .slice(0, maxLength);

const normalizeReturnStatus = (value) => toSafeText(value, 80).toLowerCase();

const readPaymentReturnPayload = (searchParams) => {
  const status = normalizeReturnStatus(
    searchParams.get("status") || searchParams.get("collection_status")
  );

  if (!status) {
    return null;
  }

  return {
    status,
    paymentId: toSafeText(searchParams.get("payment_id"), 120),
    merchantOrderId: toSafeText(searchParams.get("merchant_order_id"), 120),
    preferenceId: toSafeText(searchParams.get("preference_id"), 120),
    externalReference: toSafeText(searchParams.get("external_reference"), 160),
  };
};

const isMercadoPagoCheckoutUrl = (value) => {
  try {
    const parsedUrl = new URL(toSafeText(value, 2000));
    return (
      ["https:", "http:"].includes(toSafeText(parsedUrl.protocol, 16).toLowerCase()) &&
      toSafeText(parsedUrl.hostname, 240).toLowerCase().includes("mercadopago.")
    );
  } catch {
    return false;
  }
};

function KioskPaymentLanding() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [intent, setIntent] = useState(null);
  const redirectStartedRef = useRef(false);

  const intentId = toSafeText(searchParams.get("intent"), 120);
  const orderId = toSafeText(searchParams.get("order"), 120);
  const branchId = toSafeText(searchParams.get("branch"), 120);
  const paymentReturnPayload = useMemo(
    () => readPaymentReturnPayload(searchParams),
    [searchParams]
  );
  const returnPaymentId = paymentReturnPayload?.paymentId || "";
  const returnMerchantOrderId = paymentReturnPayload?.merchantOrderId || "";
  const returnPreferenceId = paymentReturnPayload?.preferenceId || "";
  const returnExternalReference = paymentReturnPayload?.externalReference || "";
  const returnProviderStatus = paymentReturnPayload?.status || "";
  const hasApprovedReturn = paymentReturnPayload?.status === "approved";
  const hasRejectedReturn =
    Boolean(paymentReturnPayload?.status) && paymentReturnPayload?.status !== "approved";

  useEffect(() => {
    document.title = "Abrir cobro | Mozzo";
    applyInstitutionalBrandingAssets();
  }, []);

  useEffect(() => {
    if (!intentId || !branchId) {
      setLoading(false);
      setError("Faltan datos para abrir este cobro del kiosk.");
      return undefined;
    }

    let cancelled = false;
    let intervalId = null;

    const loadIntent = async () => {
      try {
        setLoading(true);
        const nextIntent = await getPublicKioskDigitalPaymentIntent({
          intentId,
          orderId,
          branchId,
          paymentId: returnPaymentId,
          merchantOrderId: returnMerchantOrderId,
          preferenceId: returnPreferenceId,
          externalReference: returnExternalReference,
          providerStatus: returnProviderStatus,
        });

        if (cancelled) {
          return;
        }

        const checkoutUrl = toSafeText(
          nextIntent?.qr?.deeplinkUrl || nextIntent?.qr?.linkUrl,
          2000
        );
        const nextIntentApproved = APPROVED_KIOSK_PAYMENT_STATUSES.has(
          toSafeText(nextIntent?.status, 40).toLowerCase()
        );

        if (!nextIntentApproved && !isMercadoPagoCheckoutUrl(checkoutUrl)) {
          throw new Error(
            "El cobro ya no tiene un enlace valido para continuar en Mercado Pago."
          );
        }

        setIntent({
          ...nextIntent,
          qr: {
            ...(nextIntent?.qr || {}),
            deeplinkUrl: checkoutUrl,
            linkUrl: checkoutUrl,
          },
        });
        setError("");
      } catch (nextError) {
        if (cancelled) {
          return;
        }

        setError(
          nextError instanceof Error
            ? nextError.message
            : "No pudimos abrir este cobro del kiosk."
        );
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadIntent();

    if (hasApprovedReturn) {
      intervalId = window.setInterval(loadIntent, 2500);
    }

    return () => {
      cancelled = true;
      if (intervalId) {
        window.clearInterval(intervalId);
      }
    };
  }, [
    branchId,
    hasApprovedReturn,
    intentId,
    orderId,
    returnExternalReference,
    returnMerchantOrderId,
    returnPaymentId,
    returnPreferenceId,
    returnProviderStatus,
  ]);

  const checkoutUrl = toSafeText(
    intent?.qr?.deeplinkUrl || intent?.qr?.linkUrl,
    2000
  );
  const paymentAlreadyApproved = APPROVED_KIOSK_PAYMENT_STATUSES.has(
    toSafeText(intent?.status, 40).toLowerCase()
  );
  const shouldPauseAutoRedirect = Boolean(paymentReturnPayload);
  const formattedAmount = useMemo(() => {
    const amount = Number(intent?.grossAmount || 0);
    return Number.isFinite(amount)
      ? new Intl.NumberFormat("es-AR", {
          style: "currency",
          currency: "ARS",
          minimumFractionDigits: 2,
        }).format(amount)
      : "";
  }, [intent?.grossAmount]);

  const handleOpenCheckout = () => {
    if (!checkoutUrl) {
      return;
    }

    window.location.replace(checkoutUrl);
  };

  useEffect(() => {
    if (
      !checkoutUrl ||
      paymentAlreadyApproved ||
      shouldPauseAutoRedirect ||
      redirectStartedRef.current
    ) {
      return;
    }

    redirectStartedRef.current = true;
    window.location.replace(checkoutUrl);
  }, [checkoutUrl, paymentAlreadyApproved, shouldPauseAutoRedirect]);

  const title = paymentAlreadyApproved
    ? "Pago detectado"
    : hasApprovedReturn
      ? "Confirmando pago"
      : hasRejectedReturn
        ? "El pago no quedo aprobado"
        : "Abriendo Mercado Pago";
  const subtitle = paymentAlreadyApproved
    ? "Este pedido ya figura como pagado. El totem deberia cerrar la operacion solo en unos segundos."
    : hasApprovedReturn
      ? "Mercado Pago ya devolvio el aprobado. Ahora estamos avisando al totem para que envie el pedido y vuelva al inicio."
      : hasRejectedReturn
        ? "El checkout volvio sin aprobacion. Puedes abrir Mercado Pago otra vez para reintentar el cobro."
        : "Estamos entrando directamente al checkout de Mercado Pago para este pedido.";

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <img src={MOZZOAPP_INSTITUTIONAL_LOGO_URL} alt="Mozzo" style={styles.logo} />
        <div style={styles.eyebrow}>Mozzo Kiosk</div>
        <h1 style={styles.title}>{title}</h1>
        <p style={styles.subtitle}>{subtitle}</p>
        {!paymentAlreadyApproved && !hasApprovedReturn ? (
          <p style={styles.microcopy}>
            Escanealo con la camara del celular.
            <br />
            No con la app de Mercado Pago.
          </p>
        ) : null}

        {formattedAmount ? (
          <div style={styles.amountBox}>
            <span style={styles.amountLabel}>Importe</span>
            <strong style={styles.amountValue}>{formattedAmount}</strong>
          </div>
        ) : null}

        {error ? (
          <>
            <div style={styles.errorBox}>{error}</div>
            <div style={styles.actions}>
              <button
                type="button"
                style={styles.secondaryButton}
                onClick={() => window.location.reload()}
              >
                Reintentar
              </button>
            </div>
          </>
        ) : loading ? (
          <div style={styles.infoBox}>
            {hasApprovedReturn
              ? "Estamos confirmando el pago para cerrar el pedido del totem."
              : "Estamos preparando el enlace del cobro."}
          </div>
        ) : (
          <>
            <div style={styles.infoBox}>
              {hasApprovedReturn
                ? "Si el totem sigue abierto, dejalo unos segundos mas: estamos sincronizando el pago aprobado."
                : hasRejectedReturn
                  ? "Puedes volver a abrir Mercado Pago desde aqui para reintentar el cobro."
                  : "Si Mercado Pago no abre solo, toca el boton de abajo para continuar."}
            </div>
            <div style={styles.actions}>
              {!paymentAlreadyApproved && !hasApprovedReturn ? (
                <button type="button" style={styles.primaryButton} onClick={handleOpenCheckout}>
                  Abrir Mercado Pago
                </button>
              ) : null}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 24,
    background:
      "radial-gradient(circle at top, rgba(59,130,246,0.14), transparent 40%), linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%)",
  },
  card: {
    width: "min(560px, 100%)",
    display: "grid",
    gap: 18,
    justifyItems: "center",
    padding: 28,
    borderRadius: 28,
    backgroundColor: "#ffffff",
    border: "1px solid rgba(148, 163, 184, 0.22)",
    boxShadow: "0 30px 70px rgba(15, 23, 42, 0.12)",
    textAlign: "center",
  },
  logo: {
    width: 84,
    height: 84,
    objectFit: "contain",
  },
  eyebrow: {
    color: "#2563eb",
    fontSize: 12,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  },
  title: {
    margin: 0,
    color: "#0f172a",
    fontSize: 34,
    lineHeight: 1.05,
  },
  subtitle: {
    margin: 0,
    color: "#475569",
    fontSize: 16,
    lineHeight: 1.65,
  },
  microcopy: {
    margin: 0,
    color: "#64748b",
    fontSize: 13,
    lineHeight: 1.5,
    fontWeight: 600,
  },
  amountBox: {
    width: "100%",
    display: "grid",
    gap: 6,
    padding: 18,
    borderRadius: 18,
    backgroundColor: "#eff6ff",
    border: "1px solid #bfdbfe",
  },
  amountLabel: {
    color: "#1d4ed8",
    fontSize: 12,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  },
  amountValue: {
    color: "#0f172a",
    fontSize: 32,
    lineHeight: 1.1,
  },
  infoBox: {
    width: "100%",
    padding: "14px 16px",
    borderRadius: 16,
    backgroundColor: "#fff7ed",
    border: "1px solid #fed7aa",
    color: "#9a3412",
    lineHeight: 1.6,
  },
  errorBox: {
    width: "100%",
    padding: "14px 16px",
    borderRadius: 16,
    backgroundColor: "#fff1f2",
    border: "1px solid #fecdd3",
    color: "#be123c",
    lineHeight: 1.6,
  },
  actions: {
    width: "100%",
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
    justifyContent: "center",
  },
  primaryButton: {
    border: "none",
    borderRadius: 14,
    minHeight: 48,
    padding: "0 18px",
    backgroundColor: "#2563eb",
    color: "#ffffff",
    fontWeight: 700,
    cursor: "pointer",
  },
  secondaryButton: {
    border: "1px solid #cbd5e1",
    borderRadius: 14,
    minHeight: 48,
    padding: "0 18px",
    backgroundColor: "#ffffff",
    color: "#0f172a",
    fontWeight: 700,
    cursor: "pointer",
  },
};

export default KioskPaymentLanding;
